import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, User, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ParticipantForm } from '../components/participants/ParticipantForm';
import { GroupCard } from '../components/participants/GroupCard';
import { EmptyState } from '../components/shared/EmptyState';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { SwipeableCard } from '../components/shared/SwipeableCard';
import { useEvents } from '../context/EventContext';
import { useParticipantDeletion } from '../hooks/useParticipantDeletion';

export function ParticipantsTab() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const {
    currentEvent,
    addParticipant,
    deleteParticipant,
    deleteGroup,
    assignParticipantToGroup,
    updateGroup,
  } = useEvents();

  const { canDeleteParticipant } = useParticipantDeletion(currentEvent);

  const [participantToDelete, setParticipantToDelete] = useState<string | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  if (!currentEvent) return null;

  const { participants, groups } = currentEvent;

  // Participants not in any group
  const unassignedParticipants = participants.filter((p) => !p.groupId);

  const handleAddParticipant = (name: string) => {
    addParticipant(name);
  };

  const handleDeleteParticipant = (id: string) => {
    const { canDelete, reason } = canDeleteParticipant(id);
    if (!canDelete) {
      toast.error(reason || 'No se puede eliminar este participante');
      return;
    }
    setParticipantToDelete(id);
  };

  const confirmDeleteParticipant = () => {
    if (participantToDelete) {
      deleteParticipant(participantToDelete);
      setParticipantToDelete(null);
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroupToDelete(groupId);
  };

  const confirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteGroup(groupToDelete);
      setGroupToDelete(null);
    }
  };

  const handleRemoveFromGroup = (participantId: string) => {
    assignParticipantToGroup(participantId, '');
  };

  const handleSetRepresentative = (groupId: string, participantId: string) => {
    updateGroup(groupId, { representativeId: participantId });
  };

  return (
    <div className="p-4 pb-20 space-y-6">
      {/* Add participant section */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">
          Agregar participante
        </h2>
        <ParticipantForm onSubmit={handleAddParticipant} />
      </section>

      {/* Groups section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-muted-foreground">Grupos</h2>
          {unassignedParticipants.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/event/${eventId}/groups/new`)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Crear grupo
            </Button>
          )}
        </div>

        {groups.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <EmptyState
                icon={<Users className="h-8 w-8" />}
                title="Sin grupos"
                description="Los grupos permiten consolidar deudas entre parejas o familias"
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                participants={participants}
                onDelete={() => handleDeleteGroup(group.id)}
                onRemoveParticipant={handleRemoveFromGroup}
                onSetRepresentative={(pId) => handleSetRepresentative(group.id, pId)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Unassigned participants section */}
      {unassignedParticipants.length > 0 && (
        <section>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            Participantes sin grupo
          </h2>
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {unassignedParticipants.map((participant) => (
                  <SwipeableCard
                    key={participant.id}
                    onDelete={() => handleDeleteParticipant(participant.id)}
                    disabled={!canDeleteParticipant(participant.id).canDelete}
                  >
                    <li className="flex items-center justify-between py-2.5 px-6">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{participant.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteParticipant(participant.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  </SwipeableCard>
                ))}
              </ul>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground mt-2">
            Los participantes sin grupo serán tratados individualmente en el balance.
          </p>
        </section>
      )}

      {/* Empty state when no participants at all */}
      {participants.length === 0 && (
        <EmptyState
          icon={<User className="h-12 w-12" />}
          title="Sin participantes"
          description="Agrega personas para empezar a dividir gastos"
        />
      )}

      <ConfirmDialog
        open={participantToDelete !== null}
        onOpenChange={(open) => !open && setParticipantToDelete(null)}
        title="Eliminar participante"
        description="¿Estás seguro de que deseas eliminar este participante? Sus gastos asociados también serán eliminados."
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={confirmDeleteParticipant}
      />

      <ConfirmDialog
        open={groupToDelete !== null}
        onOpenChange={(open) => !open && setGroupToDelete(null)}
        title="Eliminar grupo"
        description="¿Estás seguro de que deseas eliminar este grupo? Los participantes quedarán sin grupo asignado."
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={confirmDeleteGroup}
      />
    </div>
  );
}
